import { useEffect, useRef, type KeyboardEvent } from 'react';
import { Check, ChevronDown, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Tabs from '../ui/Tabs';
import MenuButton from '../table/MenuButton';
import { useMediaQuery } from '../ui/useOverlay';
import { moreTabAria, narrowPrimary, splitCustomerTabs } from './customerTabsModel';

/**
 * A single tab in the Customer Hub. `icon` is kept for compatibility but no
 * longer rendered: fourteen icons in a row were decoration, not information.
 */
export interface CustomerTab {
  key: string;
  label: string;
  icon?: LucideIcon;
}

/**
 * The Customer Hub tab bar — the canonical underline tabs (ui/Tabs:
 * role=tablist, arrow keys, horizontal scroll on narrow screens). The caller
 * keeps the selection in the URL (`?tab=`) and passes it in.
 *
 * Calm by default (principle 0): only the `primary` tabs sit in the row; the
 * rest are behind one "עוד ▾" menu button. That button lives INSIDE the tablist
 * as a role=tab. When the open tab is one of the menu's, the button is the
 * selected tab (aria-selected=true, tabindex 0, tab-active style) and reads
 * "עוד · <name>"; the arrow keys step between it and the row tabs, and the
 * menu marks the open one as current. On a phone (< 640 px) only the first 4
 * primary tabs stay in the row (the rest go behind "עוד"), so no tab is cut
 * off; if a row still overflows it scrolls sideways with the edge fade.
 */
export default function CustomerTabs({
  tabs,
  active,
  onChange,
  primary,
}: {
  tabs: CustomerTab[];
  active: string;
  onChange: (key: string) => void;
  /** Keys that always show in the row; without it every tab does. */
  primary?: readonly string[];
}) {
  const { t } = useTranslation();
  // Below sm (640 px) only 4 tabs stay in the row so none is cut off; the rest are behind "עוד".
  const narrow = useMediaQuery('(max-width: 639px)');
  const { row, more } = splitCustomerTabs(tabs, active, narrowPrimary(primary, narrow));
  const activeMore = more.find((tab) => tab.key === active);
  const activeInMore = !!activeMore;
  // D-321: a tab from "עוד" is NOT added to the row (that pushed the row past a 360/390 phone);
  // the "עוד" button carries its name instead ("עוד · מסמכים").
  const rowTabs = activeInMore ? row.filter((tab) => tab.key !== active) : row;
  // On a phone the row scrolls sideways: keep the selected tab in view (it may
  // have just joined the row from "עוד", at the end beside the menu button).
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const tab = rowRef.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
    tab?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [active]);
  // Arrow keys on the "עוד" tab (inside the tablist) step to the neighbouring row tab, like Tabs does.
  const onMoreKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!(e.target as HTMLElement).hasAttribute('data-customer-more')) return;
    const rtl = document.documentElement.dir === 'rtl';
    const next = rtl ? 'ArrowLeft' : 'ArrowRight';
    const prev = rtl ? 'ArrowRight' : 'ArrowLeft';
    if (e.key !== next && e.key !== prev && e.key !== 'Home') return;
    const rowEls = Array.from(rowRef.current?.querySelectorAll<HTMLElement>('[role="tab"]:not([data-customer-more])') ?? []);
    const target = e.key === prev ? rowEls[rowEls.length - 1] : rowEls[0];
    if (!target) return;
    e.preventDefault();
    target.focus();
    target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    target.click();
  };
  const moreAria = moreTabAria(activeInMore);
  const { className: moreStateClass, ...moreAttrs } = moreAria;
  const moreLabel = activeMore ? `${t('uxDetails.moreTabs')} · ${activeMore.label}` : t('uxDetails.moreTabs');
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- arrow keys bubbling from the tablist and the "עוד" tab (both real tabs); the row itself is not a control
    <div ref={rowRef} onKeyDown={onMoreKey} className="flex min-w-0 items-stretch border-b border-surface-200">
      <Tabs
        ariaLabel={t('uxDetails.customerSections')}
        items={rowTabs.map((tab) => ({ key: tab.key, label: tab.label }))}
        value={active}
        onChange={onChange}
        className="min-w-0 flex-1 gap-0 border-b-0 max-sm:[&>.tab-active]:px-1.5 max-sm:[&>.tab-inactive]:px-1.5"
        trailing={more.length > 0 ? (
          <MenuButton
            label={activeMore ? moreLabel : t('uxDetails.moreSections')}
            align="start"
            popoverClassName="w-60 p-1.5"
            triggerAria={moreAttrs}
            triggerProps={{ 'data-customer-more': '' }}
            triggerClassName={`${moreStateClass} inline-flex shrink-0 items-center gap-1 max-sm:gap-0 max-sm:px-1.5 aria-expanded:text-surface-900`}
            items={more.map((tab) => {
              const current = tab.key === active;
              return {
                key: tab.key,
                label: tab.label,
                ariaLabel: current ? t('uxDetails.currentSection', { name: tab.label }) : undefined,
                trailing: current ? <Check size={16} className="text-accent-text" aria-hidden="true" /> : undefined,
                onSelect: () => onChange(tab.key),
              };
            })}
          >
            {moreLabel}
            <ChevronDown size={16} strokeWidth={1.75} aria-hidden="true" />
          </MenuButton>
        ) : undefined}
      />
    </div>
  );
}
